import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { ArrowLeft, Send, MessageSquare, Plus, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

// XSS hardening: every dangerouslySetInnerHTML in this file feeds an
// LLM-generated string into the DOM. Even though we hand-build the
// HTML inside MarkdownRenderer (only converting **bold** and `code`
// markers), the surrounding text comes from the model and could
// contain literal <script> / event-handler tags. DOMPurify allows
// only `<b>`, `<i>`, `<code>` tags (no attributes) and strips
// everything else.
const _LLM_SAFE_TAGS = ['b', 'i', 'code', 'br'];
const _LLM_SAFE_ATTRS = ['class'];
const _sanitize = (html) => DOMPurify.sanitize(html, {
  ALLOWED_TAGS: _LLM_SAFE_TAGS,
  ALLOWED_ATTR: _LLM_SAFE_ATTRS,
});

/* Shiva Linga SVG motif for chat background */
function ShivaLingaMotif({ className = '' }) {
  return (
    <svg viewBox="0 0 120 160" className={className} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="lingaGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6B8E7B" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#3d6b4e" stopOpacity="0.08" />
        </linearGradient>
        <linearGradient id="baseGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C87A5E" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#9c5a3c" stopOpacity="0.06" />
        </linearGradient>
      </defs>
      {/* Yoni base - elliptical tray */}
      <ellipse cx="60" cy="130" rx="55" ry="20" fill="url(#baseGrad)" stroke="#C87A5E" strokeWidth="0.5" strokeOpacity="0.2" />
      <ellipse cx="60" cy="125" rx="45" ry="14" fill="url(#baseGrad)" stroke="#C87A5E" strokeWidth="0.3" strokeOpacity="0.15" />
      {/* Linga - smooth dome */}
      <ellipse cx="60" cy="80" rx="28" ry="50" fill="url(#lingaGrad)" stroke="#6B8E7B" strokeWidth="0.5" strokeOpacity="0.2" />
      {/* Highlight */}
      <ellipse cx="52" cy="65" rx="10" ry="25" fill="#6B8E7B" fillOpacity="0.06" />
      {/* Third eye dot */}
      <circle cx="60" cy="55" r="3" fill="#E67E22" fillOpacity="0.25" />
    </svg>
  );
}

function MarkdownRenderer({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let inCodeBlock = false;
  let codeLines = [];
  let codeLang = '';

  lines.forEach((line, i) => {
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${i}`} className="bg-[#1a1a2e] text-green-300 text-xs rounded-lg p-3 my-2 overflow-x-auto font-mono">
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        codeLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
        codeLang = line.slice(3).trim();
      }
      return;
    }
    if (inCodeBlock) {
      codeLines.push(line);
      return;
    }
    if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="font-bold text-sm mt-3 mb-1 text-[#2C2A28]">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="font-bold text-base mt-3 mb-1 text-[#2C2A28]">{line.slice(3)}</h2>);
    } else if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(<p key={i} className="font-bold text-sm text-[#2C2A28] mt-2">{line.slice(2, -2)}</p>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const content = line.slice(2).replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/`(.*?)`/g, '<code class="bg-[#EAF0EC] px-1 rounded text-xs font-mono">$1</code>');
      elements.push(
        <li key={i} className="text-sm text-[#3a3835] ml-4 list-disc" dangerouslySetInnerHTML={{ __html: _sanitize(content) }} />
      );
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    } else {
      const content = line.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/`(.*?)`/g, '<code class="bg-[#EAF0EC] px-1 rounded text-xs font-mono">$1</code>');
      elements.push(
        <p key={i} className="text-sm text-[#3a3835] leading-relaxed" dangerouslySetInnerHTML={{ __html: _sanitize(content) }} />
      );
    }
  });

  return <div className="space-y-0.5">{elements}</div>;
}

export default function ResearchAssistant({ embedded = false }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSidebar, setShowSidebar] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/sessions`, { withCredentials: true });
      setSessions(data.sessions || []);
    } catch (e) {
      console.error('Failed to load sessions');
    }
  };

  const loadSession = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/chat/history?session_id=${sid}`, { withCredentials: true });
      setMessages((data.messages || []).map(m => ({ role: m.role, content: m.content })));
      setSessionId(sid);
    } catch (e) {
      toast.error('Failed to load conversation');
    }
  };

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
    inputRef.current?.focus();
  };

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/chat`, {
        message: msg,
        session_id: sessionId
      }, { withCredentials: true });

      if (!sessionId) setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      loadSessions();
    } catch (e) {
      toast.error('Failed to get response');
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    'What is path analysis and when should I use it?',
    'Explain D² (Mahalanobis) clustering for crop breeding',
    'How to interpret Line x Tester ANOVA results?',
    'What statistical design for comparing 10 rice varieties?'
  ];

  return (
    <div className={embedded ? "h-full flex bg-[#FAF9F7]" : "h-screen flex bg-[#FAF9F7]"} data-testid="research-assistant-page">
      {/* Sidebar */}
      {showSidebar && (
        <div className="w-72 border-r border-[#E8E6E1] bg-[#F5F4F2] flex flex-col" data-testid="chat-sidebar">
          <div className="p-4 border-b border-[#E8E6E1]">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm text-[#8A8580] hover:text-[#2C2A28] transition mb-4"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft size={16} /> Dashboard
            </button>
            <button
              onClick={newChat}
              className="w-full flex items-center justify-center gap-2 bg-[#6B8E7B] text-white rounded-xl py-2.5 text-sm font-medium hover:bg-[#5a7a69] transition"
              data-testid="new-chat-btn"
            >
              <Plus size={16} /> New Conversation
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {sessions.map((s) => (
              <button
                key={s.session_id}
                onClick={() => loadSession(s.session_id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-xs transition ${
                  sessionId === s.session_id
                    ? 'bg-[#6B8E7B]/10 text-[#3d6b4e] font-medium'
                    : 'text-[#5E5A56] hover:bg-[#EAF0EC]'
                }`}
                data-testid={`session-${s.session_id}`}
              >
                <div className="flex items-center gap-2">
                  <MessageSquare size={12} className="shrink-0 opacity-50" />
                  <span className="truncate">{s.last_message}</span>
                </div>
              </button>
            ))}
            {sessions.length === 0 && (
              <p className="text-xs text-[#B0ACA8] text-center py-8">No conversations yet</p>
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col relative overflow-hidden">
        {/* Background Motifs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-30">
          <ShivaLingaMotif className="absolute top-10 right-10 w-24 h-32" />
          <ShivaLingaMotif className="absolute bottom-20 left-10 w-20 h-28 rotate-12" />
          <ShivaLingaMotif className="absolute top-1/3 right-1/4 w-16 h-22" />
        </div>

        {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-[#E8E6E1] px-6 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-1.5 rounded-lg hover:bg-[#EAF0EC] transition text-[#5E5A56]"
              data-testid="toggle-sidebar"
            >
              <MessageSquare size={18} />
            </button>
            <div>
              <h1 className="text-lg font-bold text-[#2C2A28] tracking-tight">BK Research Assistant</h1>
              <p className="text-xs text-[#8A8580]">AI-powered agricultural statistics advisor</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-xs bg-[#EAF0EC] text-[#3d6b4e] px-2 py-1 rounded-full">
              <BookOpen size={12} /> Gemini 2.5 Flash
            </span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 z-10">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full max-w-lg mx-auto text-center" data-testid="welcome-screen">
              {/* Shiva Linga centerpiece */}
              <div className="relative mb-6">
                <ShivaLingaMotif className="w-28 h-36 opacity-50" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-[#E67E22]/20 flex items-center justify-center">
                    <BookOpen size={20} className="text-[#E67E22]" />
                  </div>
                </div>
              </div>
              <h2 className="text-xl font-bold text-[#2C2A28] mb-2">Namaste, Researcher</h2>
              <p className="text-sm text-[#8A8580] mb-6 leading-relaxed">
                I am your BK Research Assistant, here to guide you through agricultural statistics, 
                breeding designs, and biometrical analysis methods.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={`suggested-${q.slice(0, 30)}-${i}`}
                    onClick={() => {
                      setInput(q);
                      inputRef.current?.focus();
                    }}
                    className="text-left text-xs bg-white border border-[#E8E6E1] rounded-xl px-4 py-3 text-[#5E5A56] hover:border-[#6B8E7B] hover:bg-[#EAF0EC]/50 transition"
                    data-testid={`suggested-q-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((msg, i) => (
                <div
                  key={msg.id || `${msg.role}-${i}-${(msg.content || '').slice(0, 20)}`}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`chat-message-${i}`}
                >
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-[#6B8E7B] text-white rounded-br-md'
                      : 'bg-white border border-[#E8E6E1] rounded-bl-md shadow-sm'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <MarkdownRenderer text={msg.content} />
                    ) : (
                      <p className="text-sm leading-relaxed">{msg.content}</p>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start" data-testid="loading-indicator">
                  <div className="bg-white border border-[#E8E6E1] rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin text-[#6B8E7B]" />
                      <span className="text-xs text-[#8A8580]">Contemplating...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-[#E8E6E1] bg-white/80 backdrop-blur-sm px-6 py-4 z-10">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-end gap-3 bg-[#F5F4F2] rounded-2xl border border-[#E8E6E1] focus-within:border-[#6B8E7B] focus-within:ring-2 focus-within:ring-[#6B8E7B]/20 transition px-4 py-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about statistical methods, breeding designs, analysis interpretation..."
                className="flex-1 bg-transparent border-none outline-none resize-none text-sm text-[#2C2A28] placeholder:text-[#B0ACA8] min-h-[40px] max-h-[120px] py-1"
                rows={1}
                data-testid="chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                className="shrink-0 w-9 h-9 rounded-xl bg-[#6B8E7B] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#5a7a69] transition"
                data-testid="send-btn"
              >
                <Send size={16} />
              </button>
            </div>
            <p className="text-[10px] text-[#B0ACA8] text-center mt-2">
              BK Research Assistant uses AI. Verify statistical guidance with standard references.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
